// ---------------------------------
// client/src/utils/jobs.js
// Helper functions for interacting with job-related API endpoints
// ---------------------------------
import api from './api';

const GALLERY_IMAGE_MAX_DIMENSION = 1400;
const GALLERY_IMAGE_TARGET_BYTES = Math.round(1.2 * 1024 * 1024);
const GALLERY_IMAGE_MIN_COMPRESS_BYTES = 150 * 1024;
const GALLERY_IMAGE_DIMENSION_STEPS = [1400, 1200, 1000, 850, 700, 560];
const GALLERY_IMAGE_QUALITY_STEPS = [0.76, 0.68, 0.6, 0.52, 0.44, 0.36];

function loadImageFromFile(file) {
    return new Promise((resolve, reject) => {
        const image = new Image();
        const url = URL.createObjectURL(file);
        image.onload = () => {
            URL.revokeObjectURL(url);
            resolve(image);
        };
        image.onerror = () => {
            URL.revokeObjectURL(url);
            reject(new Error('image_load_failed'));
        };
        image.src = url;
    });
}

function canvasToBlob(canvas, type, quality) {
    return new Promise((resolve) => {
        canvas.toBlob(resolve, type, quality);
    });
}

function withJpegExtension(filename = 'photo.jpg') {
    const base = String(filename || 'photo').replace(/\.[^.]+$/, '') || 'photo';
    return `${base}.jpg`;
}

function extractFilename(headerValue) {
    if (!headerValue) return '';
    const match = headerValue.match(/filename\*=UTF-8''([^;]+)|filename=\"?([^\";]+)\"?/i);
    return decodeURIComponent(match?.[1] || match?.[2] || '');
}

async function compressGalleryImage(file) {
    if (
        typeof window === 'undefined' ||
        !file?.type?.startsWith('image/') ||
        file.size < GALLERY_IMAGE_MIN_COMPRESS_BYTES
    ) {
        return file;
    }
    try {
        const image = await loadImageFromFile(file);
        const width = image.naturalWidth || image.width;
        const height = image.naturalHeight || image.height;
        if (!width || !height) return file;
        let bestBlob = null;
        const dimensionSteps = GALLERY_IMAGE_DIMENSION_STEPS.filter(
            (dimension) => dimension <= GALLERY_IMAGE_MAX_DIMENSION,
        );
        for (const maxDimension of dimensionSteps) {
            const scale = Math.min(1, maxDimension / Math.max(width, height));
            const targetWidth = Math.max(1, Math.round(width * scale));
            const targetHeight = Math.max(1, Math.round(height * scale));
            const canvas = document.createElement('canvas');
            canvas.width = targetWidth;
            canvas.height = targetHeight;
            const context = canvas.getContext('2d');
            if (!context) return file;
            context.fillStyle = '#ffffff';
            context.fillRect(0, 0, targetWidth, targetHeight);
            context.drawImage(image, 0, 0, targetWidth, targetHeight);
            for (const quality of GALLERY_IMAGE_QUALITY_STEPS) {
                const blob = await canvasToBlob(canvas, 'image/jpeg', quality);
                if (!blob) continue;
                if (!bestBlob || blob.size < bestBlob.size) {
                    bestBlob = blob;
                }
                if (blob.size <= GALLERY_IMAGE_TARGET_BYTES) {
                    return new File([blob], withJpegExtension(file.name), {
                        type: 'image/jpeg',
                        lastModified: file.lastModified,
                    });
                }
            }
        }
        if (!bestBlob || bestBlob.size >= file.size) return file;
        return new File([bestBlob], withJpegExtension(file.name), {
            type: 'image/jpeg',
            lastModified: file.lastModified,
        });
    } catch {
        return file;
    }
}

// ---------------------------------
// Fetch active job types for dropdowns
// ---------------------------------
export async function fetchJobTypes() {
    const { data } = await api.get('/job-types');
    return data;
}

// ---------------------------------
// Fetch all job types (admin view, includes inactive)
// ---------------------------------
export async function fetchAllJobTypes() {
    const { data } = await api.get('/job-types/all');
    return data;
}

// ---------------------------------
// Fetch PDF templates (admin checklist source)
// ---------------------------------
export async function fetchFormTemplates() {
    const { data } = await api.get('/pdf-templates');
    return data;
}

// ---------------------------------
// Create or update job types
// ---------------------------------
export async function createJobType(payload) {
    const { data } = await api.post('/job-types', payload);
    return data;
}

export async function updateJobType(jobTypeId, payload) {
    const { data } = await api.put(`/job-types/${jobTypeId}`, payload);
    return data;
}

export async function updateJobTypeActive(jobTypeId, isActive) {
    const { data } = await api.patch(`/job-types/${jobTypeId}/active`, { isActive });
    return data;
}

export async function deleteJobType(jobTypeId) {
    await api.delete(`/job-types/${jobTypeId}`);
}

// ---------------------------------
// Fetch existing jobs assigned to the current user
// ---------------------------------
export async function fetchCurrentJobs() {
    const { data } = await api.get('/jobs/current');
    return data;
}

// ---------------------------------
// Create a new job with the required payload
// ---------------------------------
export async function createJob(payload) {
    const { data } = await api.post('/jobs', payload);
    return data;
}

// ---------------------------------
// Fetch a single job detail view
// ---------------------------------
export async function fetchJobDetail(jobId) {
    const { data } = await api.get(`/jobs/${jobId}`);
    return data;
}

// ---------------------------------
// Update job status (open/in_progress/completed/archived)
// ---------------------------------
export async function updateJobStatus(jobId, status) {
    const { data } = await api.patch(`/jobs/${jobId}/status`, { status });
    return data;
}

export async function updateJobDetails(jobId, payload) {
    const { data } = await api.patch(`/jobs/${jobId}/details`, payload);
    return data;
}

export async function deleteJob(jobId) {
    await api.delete(`/jobs/${jobId}`);
}

// ---------------------------------
// Job PDF form attachments
// ---------------------------------
export async function addJobForm(jobId, templateId) {
    const { data } = await api.post(`/jobs/${jobId}/forms`, { templateId });
    return data;
}

export async function duplicateJobForm(jobId, pdfFormId) {
    const { data } = await api.post(`/jobs/${jobId}/forms/${pdfFormId}/duplicate`);
    return data;
}

export async function removeJobForm(jobId, pdfFormId) {
    await api.delete(`/jobs/${jobId}/forms/${pdfFormId}`);
}

export async function fetchJobGallery(jobId) {
    const { data } = await api.get(`/jobs/${jobId}/gallery`);
    return data;
}

export async function downloadJobMergedPdf(jobId) {
    const response = await api.get(`/jobs/${jobId}/merged-pdf`, { responseType: 'blob' });
    return {
        blob: response.data,
        filename: extractFilename(response.headers['content-disposition']) || 'merged.pdf',
    };
}

export async function downloadJobGalleryZip(jobId) {
    const response = await api.get(`/jobs/${jobId}/gallery/download`, { responseType: 'blob' });
    return {
        blob: response.data,
        filename: extractFilename(response.headers['content-disposition']) || `${jobId}_images.zip`,
    };
}

export async function uploadJobGalleryPhoto(jobId, file) {
    const uploadFile = await compressGalleryImage(file);
    const formData = new FormData();
    formData.append('photo', uploadFile, uploadFile.name || file.name);
    const { data } = await api.post(`/jobs/${jobId}/gallery`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data;
}

export async function deleteJobGalleryPhoto(jobId, photoId) {
    await api.delete(`/jobs/${jobId}/gallery/${photoId}`);
}
